/**
 * Wrappers de máscara sobre inputs Mantine (U3 — portam a lógica de format.ts
 * para o padrão visual novo; reutilizáveis nas páginas U3 e nas que faltam em U5):
 * moeda pt-BR, percentual, hh:mm e data (DateInput pt-BR). O domínio segue em
 * centavos/ppm/minutos/ISO — a conversão acontece só na borda.
 */
import { useEffect, useState } from "react";
import { TextInput, type TextInputProps } from "@mantine/core";
import { DateInput, type DateInputProps } from "@mantine/dates";
import { fmtMoeda, parseMoeda, fmtHhMm, parseHhMm } from "../format";

/** TextInput com máscara de moeda pt-BR ↔ centavos (normaliza no blur). */
export function MoedaField({
  valor,
  onChange,
  ...props
}: {
  valor: number | null;
  onChange: (centavos: number | null) => void;
} & Omit<TextInputProps, "value" | "onChange">) {
  const [texto, setTexto] = useState(valor === null ? "" : fmtMoeda(valor));
  const [invalido, setInvalido] = useState(false);
  useEffect(() => {
    setTexto(valor === null ? "" : fmtMoeda(valor));
    setInvalido(false);
  }, [valor]);
  return (
    <TextInput
      {...props}
      value={texto}
      error={invalido ? "use 1.234,56" : props.error}
      inputMode="decimal"
      onChange={(e) => {
        const t = e.currentTarget.value;
        setTexto(t);
        if (!t.trim()) {
          setInvalido(false);
          onChange(null);
          return;
        }
        try {
          onChange(parseMoeda(t));
          setInvalido(false);
        } catch {
          setInvalido(true);
        }
      }}
      onBlur={() => {
        if (!invalido) setTexto(valor === null ? "" : fmtMoeda(valor));
        props.onBlur?.(undefined as never);
      }}
    />
  );
}

/** TextInput com máscara de percentual (2 casas) ↔ ppm. */
export function PercentField({
  valor,
  onChange,
  ...props
}: {
  valor: number | null;
  onChange: (ppm: number | null) => void;
} & Omit<TextInputProps, "value" | "onChange">) {
  const fmtPct = (ppm: number | null) =>
    ppm === null ? "" : (ppm / 10000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [texto, setTexto] = useState(fmtPct(valor));
  const [invalido, setInvalido] = useState(false);
  useEffect(() => {
    setTexto(fmtPct(valor));
    setInvalido(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);
  return (
    <TextInput
      {...props}
      value={texto}
      rightSection="%"
      error={invalido ? "use 0,00 a 100,00" : props.error}
      inputMode="decimal"
      onChange={(e) => {
        const t = e.currentTarget.value;
        setTexto(t);
        if (!t.trim()) {
          setInvalido(false);
          onChange(null);
          return;
        }
        const limpo = t.trim().replace(/\./g, "").replace(",", ".");
        if (!/^-?\d+(\.\d{1,2})?$/.test(limpo)) {
          setInvalido(true);
          return;
        }
        setInvalido(false);
        onChange(Math.round(Number(limpo) * 10000));
      }}
      onBlur={() => {
        if (!invalido) setTexto(fmtPct(valor));
      }}
    />
  );
}

/** TextInput de hh:mm (aceita negativo) ↔ minutos, com erro amigável. */
export function HhMmField({
  valor,
  onChange,
  ...props
}: {
  valor: number | null;
  onChange: (minutos: number | null) => void;
} & Omit<TextInputProps, "value" | "onChange">) {
  const [texto, setTexto] = useState(valor === null ? "" : fmtHhMm(valor));
  const [invalido, setInvalido] = useState(false);
  useEffect(() => {
    setTexto(valor === null ? "" : fmtHhMm(valor));
    setInvalido(false);
  }, [valor]);
  return (
    <TextInput
      {...props}
      value={texto}
      placeholder={props.placeholder ?? "hh:mm"}
      error={invalido ? "use hh:mm (ex.: 1:30; negativo: -0:25)" : props.error}
      onChange={(e) => {
        const t = e.currentTarget.value;
        setTexto(t);
        if (!t.trim()) {
          setInvalido(false);
          onChange(null);
          return;
        }
        try {
          onChange(parseHhMm(t));
          setInvalido(false);
        } catch {
          setInvalido(true);
        }
      }}
      onBlur={() => {
        if (!invalido) setTexto(valor === null ? "" : fmtHhMm(valor));
      }}
    />
  );
}

/** DateInput pt-BR ↔ ISO "AAAA-MM-DD" (null = vazio). */
export function DataField({
  valor,
  onChange,
  ...props
}: {
  valor: string | null;
  onChange: (iso: string | null) => void;
} & Omit<DateInputProps, "value" | "onChange">) {
  return (
    <DateInput
      placeholder="dd/mm/aaaa"
      {...props}
      value={valor ? new Date(`${valor}T12:00:00`) : null}
      onChange={(d) => {
        if (!d) {
          onChange(null);
          return;
        }
        const data = new Date(d);
        const iso = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
        onChange(iso);
      }}
      valueFormat="DD/MM/YYYY"
      locale="pt-br"
      clearable
    />
  );
}

// ---------------------------------------------------------------------------
// Competência MM/AAAA (seletor — nunca digitar AAAAMM)
// ---------------------------------------------------------------------------

const MESES = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

/** Seletor mês/ano → competência Int AAAAMM. */
export function CompetenciaInput({
  valor,
  onChange,
  anoMin,
  anoMax,
}: {
  /** AAAAMM (null = sem seleção). */
  valor: number | null;
  onChange: (comp: number) => void;
  anoMin?: number;
  anoMax?: number;
}) {
  const anoAtual = new Date().getUTCFullYear();
  const min = anoMin ?? anoAtual - 2;
  const max = anoMax ?? anoAtual + 2;
  const anos: number[] = [];
  for (let a = min; a <= max; a++) anos.push(a);
  const mes = valor ? valor % 100 : 0;
  const ano = valor ? Math.floor(valor / 100) : 0;

  return (
    <span className="competencia-input">
      <select
        aria-label="mês"
        value={mes || ""}
        onChange={(e) => {
          const m = Number(e.target.value);
          if (m && ano) onChange(ano * 100 + m);
          else if (m) onChange(anoAtual * 100 + m);
        }}
      >
        <option value="" disabled>
          mês
        </option>
        {MESES.map((m) => (
          <option key={m} value={Number(m)}>
            {m}
          </option>
        ))}
      </select>
      <span>/</span>
      <select
        aria-label="ano"
        value={ano || ""}
        onChange={(e) => {
          const a = Number(e.target.value);
          if (a && mes) onChange(a * 100 + mes);
          else if (a) onChange(a * 100 + 1);
        }}
      >
        <option value="" disabled>
          ano
        </option>
        {anos.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </span>
  );
}

// ---------------------------------------------------------------------------
// hh:mm (minutos na borda) com validação amigável
// ---------------------------------------------------------------------------

/** "2:00" ↔ 120 min (aceita negativos "-0:25"); erro amigável embaixo do campo. */
export function HhMmInput({
  valor,
  onChange,
  size,
}: {
  /** Minutos inteiros (null = vazio). */
  valor: number | null;
  onChange: (minutos: number | null) => void;
  size?: number;
}) {
  const [texto, setTexto] = useState(valor === null ? "" : fmtHhMm(valor));
  const [erro, setErro] = useState("");
  useEffect(() => {
    setTexto(valor === null ? "" : fmtHhMm(valor));
    setErro("");
  }, [valor]);

  return (
    <span className="hhmm-input">
      <input
        className={erro ? "input-invalido" : ""}
        value={texto}
        placeholder="hh:mm"
        size={size ?? 7}
        onChange={(e) => {
          const t = e.target.value;
          setTexto(t);
          if (!t.trim()) {
            setErro("");
            onChange(null);
            return;
          }
          try {
            onChange(parseHhMm(t));
            setErro("");
          } catch {
            setErro("use hh:mm (ex.: 1:30; negativo: -0:25)");
          }
        }}
        onBlur={() => {
          if (!erro) setTexto(valor === null ? "" : fmtHhMm(valor));
        }}
      />
      {erro && <span style={{ fontSize: 11, color: "var(--mantine-color-red-7)" }}>{erro}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Competência MM/AAAA (seletor — nunca digitar AAAAMM)
// ---------------------------------------------------------------------------

const MESES_COMP = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

/** Seletor mês/ano → competência Int AAAAMM (nativo — leve e acessível). */
export function CompetenciaField({
  valor,
  onChange,
  anoMin,
  anoMax,
  label,
  description,
}: {
  /** AAAAMM (null = sem seleção). */
  valor: number | null;
  onChange: (comp: number) => void;
  anoMin?: number;
  anoMax?: number;
  label?: string;
  description?: string;
}) {
  const anoAtual = new Date().getUTCFullYear();
  const min = anoMin ?? anoAtual - 2;
  const max = anoMax ?? anoAtual + 2;
  const anos: number[] = [];
  for (let a = min; a <= max; a++) anos.push(a);
  const mes = valor ? valor % 100 : 0;
  const ano = valor ? Math.floor(valor / 100) : 0;

  const conteudo = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <select
        aria-label="mês"
        value={mes || ""}
        onChange={(e) => {
          const m = Number(e.currentTarget.value);
          if (m && ano) onChange(ano * 100 + m);
          else if (m) onChange(anoAtual * 100 + m);
        }}
        style={{ font: "inherit", padding: "3px 6px", border: "1px solid #b9c0c8", borderRadius: 4 }}
      >
        <option value="" disabled>
          mês
        </option>
        {MESES_COMP.map((m) => (
          <option key={m} value={Number(m)}>
            {m}
          </option>
        ))}
      </select>
      <span>/</span>
      <select
        aria-label="ano"
        value={ano || ""}
        onChange={(e) => {
          const a = Number(e.currentTarget.value);
          if (a && mes) onChange(a * 100 + mes);
          else if (a) onChange(a * 100 + 1);
        }}
        style={{ font: "inherit", padding: "3px 6px", border: "1px solid #b9c0c8", borderRadius: 4 }}
      >
        <option value="" disabled>
          ano
        </option>
        {anos.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </span>
  );

  if (!label) return conteudo;
  return (
    <label style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      {conteudo}
      {description && <span style={{ fontSize: 12, color: "#6b7280" }}>{description}</span>}
    </label>
  );
}
